import React, { Component } from 'react';
import Fade from 'react-reveal/Fade';
import yaml from 'js-yaml';

import '../css/WhyNexuniSection.css';

class WhyNexuniSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      hasAnimated: false,
      latestPost: null,
      sectionTitle: {
        "zh": "為什麼選擇 Nexuni？",
        "en": "Why Nexuni?",
        "ja": "Nexuniを選ぶ理由？"
      },
      cards: [
        {
          id: "roi",
          title: {
            "zh": "快速降本增效",
            "en": "Cut Costs, Boost Output",
            "ja": "コスト削減・生産性向上"
          },
          desc: {
            "zh": "AI 驅動的自動化大幅降低對專業人力的依賴，投資回收期在六個月內。",
            "en": "AI-driven automation dramatically reduces reliance on skilled labor, with return on investment in under 6 months.",
            "ja": "AI 駆動の自動化で熟練労働者への依存を大幅に削減、6ヶ月以内の投資回収を実現。"
          },
          highlights: [
            { value: 88, suffix: "%", label: { "zh": "人力成本節省", "en": "Labor Cost Savings", "ja": "人件費削減" } },
            { value: 150, suffix: "%", label: { "zh": "生產力提升", "en": "Productivity Boost", "ja": "生産性向上" } }
          ]
        },
        {
          id: "proven",
          title: {
            "zh": "大規模實績驗證",
            "en": "Proven at Scale",
            "ja": "大規模な実績"
          },
          desc: {
            "zh": "超過 100 套機器人與自動化系統已在工廠、社區、停車場等真實場域穩定運行。",
            "en": "Over 100 robots and automation systems are operating reliably across factories, communities, and parking facilities.",
            "ja": "100 以上のロボットと自動化システムが工場・集合住宅・駐車場など実環境で安定稼働中。"
          },
          heroStat: { value: 100, suffix: "+", label: { "zh": "系統已部署", "en": "Systems Deployed", "ja": "システム導入済み" } }
        },
        {
          id: "certified",
          title: {
            "zh": "認證技術夥伴",
            "en": "Certified Technology Partner",
            "ja": "認定テクノロジーパートナー"
          },
          desc: {
            "zh": "身為 NVIDIA Inception Program 成員與 Universal Robots 認證解決方案供應商，我們深度整合 NVIDIA AI 平台與 UR 協作機器人，打造可靠的智慧自動化方案。",
            "en": "As an NVIDIA Inception Program member and Universal Robots Certified Solution Provider, we deeply integrate NVIDIA AI platforms and UR cobots to deliver reliable intelligent automation.",
            "ja": "NVIDIA Inception Program メンバーおよび Universal Robots 認定ソリューションプロバイダーとして、NVIDIA AI プラットフォームと UR 協働ロボットを深く統合し、信頼性の高いスマート自動化を実現。"
          },
          logos: [
            { src: "/image/inception.png", alt: "NVIDIA Inception", extraClass: "why-nexuni-card-logo-lg" },
            { src: "/logo/logowall_ur.png", alt: "Universal Robots" }
          ]
        }
      ],
      newsLabel: {
        "zh": "最新消息",
        "en": "Latest News",
        "ja": "最新ニュース"
      },
      newsReadMore: {
        "zh": "觀看影片",
        "en": "Watch Video",
        "ja": "動画を見る"
      }
    };

    this.cardsRef = React.createRef();
  }

  componentDidMount() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.state.hasAnimated) {
            this.setState({ hasAnimated: true });
            this.animateAllCounters();
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (this.cardsRef.current) {
      this.observer.observe(this.cardsRef.current);
    }

    fetch(process.env.PUBLIC_URL + '/yaml/blog_posts.yaml')
      .then(res => res.text())
      .then(text => {
        const parsed = yaml.load(text);
        if (parsed && parsed.posts && parsed.posts.length > 0) {
          const dogPost = parsed.posts.find(p => p.id === 'collie_sg_launch');
          this.setState({ latestPost: dogPost || parsed.posts[0] });
        }
      })
      .catch(() => {});
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  animateCounter(el) {
    const target = parseInt(el.dataset.whyCounter, 10);
    el.textContent = '0';
    let startTimestamp = null;
    const duration = target > 100 ? 1500 : 1000;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.floor(eased * target);
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.textContent = target;
      }
    };
    window.requestAnimationFrame(step);
  }

  animateAllCounters() {
    document.querySelectorAll('[data-why-counter]').forEach(el => {
      this.animateCounter(el);
    });
  }

  handleStatHover(e) {
    const counter = e.currentTarget.querySelector('[data-why-counter]');
    if (counter) {
      this.animateCounter(counter);
    }
  }

  render() {
    const lang = this.state.lang;
    const post = this.state.latestPost;

    return (
      <div className="why-nexuni-section">
        <div className="why-nexuni-inner">

          <Fade duration={800} delay={200}>
            <h2 className="why-nexuni-title">{this.state.sectionTitle[lang]}</h2>
          </Fade>

          {/* Three reason cards */}
          <div className="why-nexuni-cards" ref={this.cardsRef}>
            {this.state.cards.map((card, index) => (
              <Fade duration={800} delay={300 + index * 120} key={card.id}>
                <div className="why-nexuni-card">
                  <div className="why-nexuni-card-accent" />
                  <h3>{card.title[lang]}</h3>
                  <p className="why-nexuni-card-desc">{card.desc[lang]}</p>

                  {/* Card 1: partner logos */}
                  {card.logos && (
                    <div className="why-nexuni-card-logos">
                      {card.logos.map((logo, li) => (
                        <img key={li} src={logo.src} alt={logo.alt} className={"why-nexuni-card-logo" + (logo.extraClass ? " " + logo.extraClass : "")} />
                      ))}
                    </div>
                  )}

                  {/* Card 2: inline stat highlights */}
                  {card.highlights && (
                    <div className="why-nexuni-card-highlights">
                      {card.highlights.map((h, hi) => (
                        <div className="why-nexuni-card-hl" key={hi} onMouseEnter={(e) => this.handleStatHover(e)}>
                          <span className="why-nexuni-card-hl-value">
                            <span data-why-counter={h.value}>0</span>{h.suffix}
                          </span>
                          <span className="why-nexuni-card-hl-label">{h.label[lang]}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Card 3: hero stat */}
                  {card.heroStat && (
                    <div className="why-nexuni-card-hero-stat" onMouseEnter={(e) => this.handleStatHover(e)}>
                      <span className="why-nexuni-card-hero-value">
                        <span data-why-counter={card.heroStat.value}>0</span>{card.heroStat.suffix}
                      </span>
                      <span className="why-nexuni-card-hero-label">{card.heroStat.label[lang]}</span>
                    </div>
                  )}
                </div>
              </Fade>
            ))}
          </div>

          {/* Latest news — light card, clearly below */}
          {post && (
            <Fade duration={800} delay={700}>
              <div
                className="why-nexuni-news"
                onClick={() => window.open('https://www.youtube.com/watch?v=gPjM8f-4x7w', '_blank')}
              >
                <div className="why-nexuni-news-img-wrap">
                  <img src={post.image} alt={post.title ? post.title[lang] : ''} />
                </div>
                <div className="why-nexuni-news-body">
                  <span className="why-nexuni-news-badge">{this.state.newsLabel[lang]}</span>
                  <h3 className="why-nexuni-news-title">{post.title ? post.title[lang] : ''}</h3>
                  <span className="why-nexuni-news-date">{post.date}</span>
                  <span className="why-nexuni-news-link">{this.state.newsReadMore[lang]} &rarr;</span>
                </div>
              </div>
            </Fade>
          )}

        </div>
      </div>
    );
  }
}

export default WhyNexuniSection;
